import { Link, useRouteContext } from "@tanstack/react-router";
import { type FragmentType, graphql, useFragment } from "@repo/graphql-types";
import { Share as CapacitorShare } from "@capacitor/share";
import { OnelivV2, Share } from "@repo/icons";
import { CpsToast } from "corpus";
import { useState } from "react";
import {
  ColoredCardInfo,
  ColoredCardInfoButton,
  ColoredCardInfoDescription,
  ColoredCardInfoHeader,
  ColoredCardInfoTitle,
} from "@/components/colored-card-info";
import { trackEvent } from "@/lib/tracking";

export const HomeSectionOneLivActiveFragment = graphql(/* GraphQL */ `
  fragment HomeSectionOneLivActiveFragment on query_root {
    LocusGetProfileCompletionPercentage {
      porcentagemPerfil
      pendente {
        experiencias
        faqs
        focosTratamentos
        membroFormacoes
        sobre
      }
      membro {
        slug
      }
    }
  }
`);

interface HomeSectionOneLivActiveProps {
  data: FragmentType<typeof HomeSectionOneLivActiveFragment>;
}

export const HomeSectionOneLivActive = ({
  data,
}: HomeSectionOneLivActiveProps): JSX.Element => {
  const [openCopiedLinkToast, setOpenCopiedLinkToast] = useState(false);
  const { user } = useRouteContext({ strict: false });

  const fragmentData = useFragment(HomeSectionOneLivActiveFragment, data);

  const completionPercentage =
    fragmentData.LocusGetProfileCompletionPercentage?.porcentagemPerfil ?? 0;

  const membroSlug = fragmentData.LocusGetProfileCompletionPercentage?.membro?.slug;

  const shareLink = async (): Promise<void> => {
    trackEvent("Oneliv Perfil Compartilhado", {
      codUsuario: user.codUsuario,
    });

    const url = `https://oneliv.com.br/profissional/${membroSlug}?utm_source=app-shared-link`;

    const canShare = await CapacitorShare.canShare();
    if (!canShare.value) {
      await navigator.clipboard.writeText(url);
      setOpenCopiedLinkToast(true);
    }

    await CapacitorShare.share({
      title: "Conheça meu perfil no OneLiv!",
      text: "Olá! Gostaria de compartilhar com você o link do meu perfil no OneLiv. Através desse link, você poderá agendar consultas comigo de maneira rápida e fácil!",
      url,
    });
  };

  return (
    <>
      {completionPercentage < 80 ? (
        <Link to="/oneliv/welcome">
          <ColoredCardInfo className="bg-white">
            <ColoredCardInfoHeader>
              <ColoredCardInfoTitle className="text-[#4F4FF7]">
                Complete o seu perfil Oneliv
              </ColoredCardInfoTitle>
              <ColoredCardInfoDescription className="text-[#4F4FF7]">
                Esteja presente onde os pacientes buscam por você. Perfis completos geram
                até 2x mais consultas!
              </ColoredCardInfoDescription>
            </ColoredCardInfoHeader>
            <ColoredCardInfoButton className="bg-[#6EE7B7] text-[#4F4FF7]">
              Preencher agora :)
            </ColoredCardInfoButton>
          </ColoredCardInfo>
        </Link>
      ) : (
        <Link
          onClick={() => {
            void shareLink();
          }}
        >
          <ColoredCardInfo className="bg-[#07079C]">
            <ColoredCardInfoHeader>
              <ColoredCardInfoTitle className="text-white">
                Compartilhe seu perfil
              </ColoredCardInfoTitle>
              <ColoredCardInfoDescription className="text-white">
                Seu perfil Oneliv é o seu novo cartão de visitas digital! Aumente seu
                alcance compartilhando seu perfil completo.
              </ColoredCardInfoDescription>
              <div className="flex flex-row justify-between items-center bg-neutral-50 w-full py-4 px-3 rounded-lg mt-4">
                <div className="flex flex-row items-center justify-start gap-2">
                  <OnelivV2 size={24} />
                  <p className="text-xs font-medium text-neutral-500">
                    Compartilhar meu perfil
                  </p>
                </div>

                <Share className="fill-secondary-400" size={24} />
              </div>
            </ColoredCardInfoHeader>
          </ColoredCardInfo>
        </Link>
      )}

      <CpsToast
        title="Link copiado para a área de transferência"
        show={openCopiedLinkToast}
        onClose={() => setOpenCopiedLinkToast(false)}
        type="success"
        duration={10}
      />
    </>
  );
};
